import React, { useMemo } from "react";
import { useLazyPlantOptions } from "../../features/plants/usePlantOptions";
import {
  useAddSettingsMutation,
  useDeleteSettingsMutation,
  useGetSettingsQuery,
  useUpdateSettingsMutation,
} from "../../features/settings/settingsApi";
import MasterCrudPage from "../masters/MasterCrudPage";
import { withAuditFilters } from "../masters/filterMetadata";
import { getPlantLabel, renderPlantCell } from "../masters/masterMetadata";

const getSettingsQrValue = (row) => row?.qr_value || row?.setting_id;
const getSettingsQrDisplayValue = (row) => row?.qr_display_text || row?.setting_id;
const settingsFilterFields = withAuditFilters([
  { name: "setting_id", label: "Setting ID", optionSource: "settings" },
  {
    name: "meal_gap_hours",
    label: "Meal Gap Hours",
    type: "number",
    min: 0,
    step: 1,
  },
]);

const Settings = () => {
  const {
    options: plantOptions,
    isFetching: isLoadingPlants,
    loadOptions: loadPlantOptions,
  } = useLazyPlantOptions();

  const fields = useMemo(
    () => [
      {
        name: "plant_id",
        label: "Plant",
        type: "select",
        options: plantOptions,
        placeholder: isLoadingPlants ? "Loading plants..." : "Select plant",
        disabled: isLoadingPlants && plantOptions.length === 0,
        disabledOnEdit: true,
        required: true,
        fullWidth: true,
      },
      {
        name: "meal_gap_hours",
        label: "Meal Gap Hours",
        type: "number",
        min: 0,
        step: 1,
        placeholder: "Example: 3",
        required: true,
        fullWidth: true,
      },
    ],
    [isLoadingPlants, plantOptions]
  );

  const columns = useMemo(
    () => (metaData) => [
      {
        name: "Setting ID",
        selector: (row) => row?.setting_id || "-",
        sortable: true,
      },
      {
        name: "Plant",
        selector: (row) => getPlantLabel(row, metaData),
        cell: (row) => renderPlantCell(row, metaData),
        sortable: true,
        grow: 2,
      },
      {
        name: "Meal Gap Hours",
        selector: (row) => row?.meal_gap_hours ?? "-",
        sortable: true,
      },
    ],
    []
  );

  return (
    <MasterCrudPage
      entityName="Setting"
      pageTitle="Settings"
      idField="setting_id"
      displayField="setting_id"
      fields={fields}
      columns={columns}
      searchFields={[
        "setting_id",
        "plant_id",
        "meal_gap_hours",
        (row, metaData) => getPlantLabel(row, metaData),
      ]}
      searchPlaceholder="Search settings"
      useListQuery={useGetSettingsQuery}
      useCreateMutation={useAddSettingsMutation}
      useUpdateMutation={useUpdateSettingsMutation}
      useDeleteMutation={useDeleteSettingsMutation}
      filterFields={settingsFilterFields}
      qrDisplayValue={getSettingsQrDisplayValue}
      qrValue={getSettingsQrValue}
      transformPayload={(payload, editData) => {
        if (!editData) return payload;
        const updatePayload = { ...payload };
        delete updatePayload.plant_id;
        return updatePayload;
      }}
      onOpenForm={loadPlantOptions}
      noDataLabel="No settings found"
    />
  );
};

export default Settings;
